import React, { useState, useEffect, useCallback } from "react";
import {
  Phone,
  Edit2,
  X,
  Loader2,
  Check,
  AlertCircle,
  Users,
  Clock,
  DollarSign,
  PhoneIncoming,
  PhoneOutgoing,
  ChevronLeft,
  BarChart3,
  History,
  PhoneCall
} from "lucide-react";
import {
  getPhoneNumbers,
  updatePhoneNumber,
  getPhoneUsage,
  getPhoneUsageHistory,
  getPhoneCallLogs
} from "../api/phone";
import { fetchTeams } from "../api/teams";

/**
 * Format phone number for display
 * +19728856667 -> +1 (972) 885-6667
 */
const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

/**
 * Format cents to dollars
 */
const formatCurrency = (cents) => {
  return `$${(cents / 100).toFixed(2)}`;
};

/**
 * Format duration in seconds to minutes:seconds
 */
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Format date for display
 */
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateOnly.getTime() === today.getTime()) {
    return "Today";
  }
  if (dateOnly.getTime() === yesterday.getTime()) {
    return "Yesterday";
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Format time for display
 */
const formatTime = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

// ============================================
// Phone Number Card Component
// ============================================
const PhoneNumberCard = ({ phone, onEdit, onViewUsage, onViewCalls }) => {
  const isActive = phone.status === "active";

  return (
    <div className="border dark:border-slate-700 border-slate-200 rounded-xl p-4 dark:bg-slate-800/50 bg-white hover:border-sky-500/50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${isActive ? "bg-emerald-500/10" : "bg-slate-500/10"}`}>
            <Phone className={`w-5 h-5 ${isActive ? "text-emerald-500" : "text-slate-400"}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold dark:text-white text-slate-900">
                {formatPhoneNumber(phone.phone_number)}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                }`}
              >
                {isActive ? "Active" : "Suspended"}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {phone.display_name || "Unnamed"}
            </p>
          </div>
        </div>
        <button
          onClick={() => onEdit(phone)}
          className="p-2 rounded-lg text-slate-400 hover:text-sky-500 hover:bg-sky-500/10 transition-colors"
          title="Edit"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-4 pt-4 border-t dark:border-slate-700 border-slate-200">
        <div className="flex items-center gap-2 text-sm">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-slate-500 dark:text-slate-400">Team:</span>
          <span className={phone.team_name ? "dark:text-white text-slate-900" : "text-slate-400"}>
            {phone.team_name || "Not assigned"}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
          <button
            onClick={() => onViewUsage(phone)}
            className="flex items-center gap-1.5 hover:text-sky-500 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            <span>Usage</span>
          </button>
          <button
            onClick={() => onViewCalls(phone)}
            className="flex items-center gap-1.5 hover:text-sky-500 transition-colors"
          >
            <History className="w-4 h-4" />
            <span>Call Logs</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Edit Phone Modal
// ============================================
const EditPhoneModal = ({ phone, teams, onClose, onSave }) => {
  const [displayName, setDisplayName] = useState(phone.display_name || "");
  const [teamId, setTeamId] = useState(phone.team_id || "");
  const [status, setStatus] = useState(phone.status || "active");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const selectedTeam = teams.find((t) => t.id === teamId);
      await updatePhoneNumber(phone.id, {
        display_name: displayName,
        team_id: teamId || null,
        team_name: selectedTeam?.name || null,
        status
      });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl dark:bg-slate-900 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b dark:border-slate-700 border-slate-200 p-4">
          <h3 className="font-semibold dark:text-white text-slate-900">Edit Phone Number</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Phone Number (readonly) */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Phone Number
            </label>
            <div className="px-3 py-2.5 rounded-lg dark:bg-slate-800 bg-slate-100 text-slate-500 dark:text-slate-400">
              {formatPhoneNumber(phone.phone_number)}
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Main Sales Line"
              className="w-full px-3 py-2.5 rounded-lg dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          {/* Assigned Team */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Assigned Team
            </label>
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg dark:bg-slate-800 bg-slate-50 border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">Not assigned</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Status
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={status === "active"}
                  onChange={() => setStatus("active")}
                  className="w-4 h-4 text-sky-500"
                />
                <span className="text-sm dark:text-white text-slate-900">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="suspended"
                  checked={status === "suspended"}
                  onChange={() => setStatus("suspended")}
                  className="w-4 h-4 text-sky-500"
                />
                <span className="text-sm dark:text-white text-slate-900">Suspended</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t dark:border-slate-700 border-slate-200 p-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sky-500 hover:bg-sky-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Usage Dashboard View
// ============================================
const UsageDashboardView = ({ phone, onBack }) => {
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUsage = async () => {
      try {
        const [usageData, historyData] = await Promise.all([
          getPhoneUsage(phone.id),
          getPhoneUsageHistory(phone.id, 6)
        ]);
        setUsage(usageData);
        setHistory(historyData.history || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadUsage();
  }, [phone.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-400">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 text-sky-500 hover:text-sky-400 font-medium"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Calculate max value for chart scaling
  const maxCost = Math.max(...history.map((h) => h.cost_cents), 100);

  return (
    <div>
      {/* Header */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-sky-500 transition-colors mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        <span>Back to Phone Numbers</span>
      </button>

      <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">
        {formatPhoneNumber(phone.phone_number)} Usage
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {phone.display_name || "Phone Number"}
      </p>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-1">
            <Clock className="w-4 h-4" />
            This Month
          </div>
          <div className="text-2xl font-bold dark:text-white text-slate-900">
            {usage?.total_minutes || 0} <span className="text-sm font-normal text-slate-500">min</span>
          </div>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
            Cost
          </div>
          <div className="text-2xl font-bold dark:text-white text-slate-900">
            {formatCurrency(usage?.cost_cents || 0)}
          </div>
        </div>
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm mb-1">
            <PhoneCall className="w-4 h-4" />
            Total
          </div>
          <div className="text-2xl font-bold dark:text-white text-slate-900">
            {usage?.total_calls || 0} <span className="text-sm font-normal text-slate-500">calls</span>
          </div>
        </div>
      </div>

      {/* Usage Chart */}
      {history.length > 0 && (
        <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200 mb-6">
          <h4 className="font-medium dark:text-white text-slate-900 mb-4">Usage History</h4>
          <div className="flex items-end justify-between gap-2 h-32">
            {history.map((month) => {
              const height = maxCost > 0 ? (month.cost_cents / maxCost) * 100 : 0;
              return (
                <div key={month.period} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full bg-sky-500/80 rounded-t transition-all"
                    style={{ height: `${Math.max(height, 4)}%` }}
                    title={`${formatCurrency(month.cost_cents)}`}
                  />
                  <span className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {new Date(month.period + "-01").toLocaleDateString("en-US", { month: "short" })}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Breakdown */}
      <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
        <h4 className="font-medium dark:text-white text-slate-900 mb-4">Breakdown</h4>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <PhoneIncoming className="w-4 h-4" /> Inbound
            </span>
            <span className="dark:text-white text-slate-900">
              {usage?.inbound_minutes || 0} min ({formatCurrency(usage?.inbound_cost_cents || 0)})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <PhoneOutgoing className="w-4 h-4" /> Outbound
            </span>
            <span className="dark:text-white text-slate-900">
              {usage?.outbound_minutes || 0} min ({formatCurrency(usage?.outbound_cost_cents || 0)})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500 dark:text-slate-400">+ Number Fee</span>
            <span className="dark:text-white text-slate-900">{formatCurrency(usage?.number_fee_cents || 100)}</span>
          </div>
          <div className="pt-2 border-t dark:border-slate-700 border-slate-200 flex justify-between font-medium">
            <span className="dark:text-white text-slate-900">Total</span>
            <span className="text-sky-500">{formatCurrency(usage?.cost_cents || 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Call Logs View
// ============================================
const CallLogsView = ({ phone, onBack }) => {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCalls = async () => {
      try {
        const data = await getPhoneCallLogs(phone.id, { limit: 50 });
        setCalls(data.calls || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadCalls();
  }, [phone.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-400">{error}</p>
        <button
          onClick={onBack}
          className="mt-4 text-sky-500 hover:text-sky-400 font-medium"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Group calls by date
  const groupedCalls = calls.reduce((groups, call) => {
    const date = formatDate(call.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(call);
    return groups;
  }, {});

  return (
    <div>
      {/* Header */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-sky-500 transition-colors mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        <span>Back to Phone Numbers</span>
      </button>

      <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">
        Call History
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
        {formatPhoneNumber(phone.phone_number)} - {phone.display_name || "Phone Number"}
      </p>

      {/* Call List */}
      {calls.length === 0 ? (
        <div className="text-center py-12">
          <PhoneCall className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">No calls recorded yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedCalls).map(([date, dateCalls]) => (
            <div key={date}>
              <h4 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
                {date}
              </h4>
              <div className="space-y-2">
                {dateCalls.map((call) => (
                  <div
                    key={call.id}
                    className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${
                            call.direction === "inbound"
                              ? "bg-emerald-500/10"
                              : "bg-sky-500/10"
                          }`}
                        >
                          {call.direction === "inbound" ? (
                            <PhoneIncoming
                              className="w-4 h-4 text-emerald-500"
                            />
                          ) : (
                            <PhoneOutgoing className="w-4 h-4 text-sky-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium dark:text-white text-slate-900">
                            {call.direction === "inbound" ? "Inbound from" : "Outbound to"}{" "}
                            {formatPhoneNumber(
                              call.direction === "inbound"
                                ? call.caller_number
                                : call.callee_number
                            )}
                          </div>
                          <div className="text-sm text-slate-500 dark:text-slate-400">
                            {call.agent_email || "Unknown"} &bull;{" "}
                            {formatDuration(call.duration_seconds)} &bull;{" "}
                            {formatCurrency(call.cost_cents)}
                          </div>
                        </div>
                      </div>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {formatTime(call.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Main Component
// ============================================
const PhoneNumbersManager = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal/View states
  const [editingPhone, setEditingPhone] = useState(null);
  const [viewingUsage, setViewingUsage] = useState(null);
  const [viewingCalls, setViewingCalls] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [phonesData, teamsData] = await Promise.all([
        getPhoneNumbers(),
        fetchTeams().catch(() => ({ teams: [] }))
      ]);
      setPhoneNumbers(phonesData.numbers || []);
      setTeams(teamsData.teams || teamsData || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditSave = () => {
    setEditingPhone(null);
    loadData(); // Refresh the list
  };

  // Show usage view
  if (viewingUsage) {
    return (
      <UsageDashboardView
        phone={viewingUsage}
        onBack={() => setViewingUsage(null)}
      />
    );
  }

  // Show call logs view
  if (viewingCalls) {
    return (
      <CallLogsView phone={viewingCalls} onBack={() => setViewingCalls(null)} />
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-slate-500 dark:text-slate-400">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold dark:text-white text-slate-900">
            Phone Numbers
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage phone numbers for your teams
          </p>
        </div>
      </div>

      {/* Phone Numbers List */}
      {phoneNumbers.length === 0 ? (
        <div className="text-center py-12 border dark:border-slate-700 border-slate-200 rounded-xl">
          <Phone className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mb-2">
            No phone numbers yet
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            Contact support to provision a phone number for your account
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {phoneNumbers.map((phone) => (
            <PhoneNumberCard
              key={phone.id}
              phone={phone}
              onEdit={setEditingPhone}
              onViewUsage={setViewingUsage}
              onViewCalls={setViewingCalls}
            />
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingPhone && (
        <EditPhoneModal
          phone={editingPhone}
          teams={teams}
          onClose={() => setEditingPhone(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default PhoneNumbersManager;
